import React from 'react';
import { Link } from 'react-router-dom';
import { Container, CheckCircle, ArrowRight, Award, Zap, Shield, TrendingUp, Layers, Settings, Network } from 'lucide-react';

export default function KubernetesManagement() {
  const benefits = [
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Auto-Scaling",
      description: "Horizontal and vertical pod autoscaling based on CPU, memory, and custom metrics"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "High Availability",
      description: "Multi-zone clusters with automatic failover and self-healing capabilities"
    },
    {
      icon: <Network className="w-6 h-6" />,
      title: "Service Mesh",
      description: "Istio and Linkerd implementation for advanced traffic management and observability"
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "GitOps Deployment",
      description: "ArgoCD and Flux for declarative, version-controlled deployments"
    }
  ];

  const technologies = [
    { name: "Orchestration", items: ["Kubernetes", "AWS EKS", "Azure AKS", "GKE", "k3s"] },
    { name: "Service Mesh", items: ["Istio", "Linkerd", "Consul", "Envoy"] },
    { name: "Package Management", items: ["Helm", "Kustomize", "Helmfile"] },
    { name: "Monitoring", items: ["Prometheus", "Grafana", "Jaeger", "Kiali"] }
  ];

  const features = [
    {
      category: "Cluster Setup & Management",
      items: [
        "Production-grade EKS/AKS cluster provisioning",
        "Multi-zone high availability configuration",
        "RBAC and security policy implementation",
        "Cluster autoscaling and node pool management"
      ]
    },
    {
      category: "Application Deployment",
      items: [
        "Helm chart development and management",
        "GitOps workflows with ArgoCD",
        "Blue-green and canary deployments",
        "Secrets management with Sealed Secrets/External Secrets"
      ]
    },
    {
      category: "Networking & Security",
      items: [
        "Ingress controller configuration (NGINX, Traefik)",
        "Network policies and pod security policies",
        "Service mesh implementation",
        "TLS certificate management with cert-manager"
      ]
    },
    {
      category: "Observability",
      items: [
        "Prometheus and Grafana setup",
        "Centralized logging with ELK/Loki",
        "Distributed tracing with Jaeger",
        "Custom metrics and alerting"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=1920&auto=format&fit=crop&q=80"
            alt="Kubernetes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <Container className="w-4 h-4 text-blue-400" />
              <span>Container Orchestration</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Kubernetes & Container Management
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Production-ready Kubernetes deployments with auto-scaling, service mesh,
              and comprehensive monitoring for microservices architecture.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Kubernetes?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The industry-standard platform for container orchestration and microservices
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Comprehensive Kubernetes Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end Kubernetes solutions from cluster setup to production operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-purple-600">{feature.category}</h3>
                <ul className="space-y-3">
                  {feature.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Production-Grade Kubernetes Expertise
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I've managed 30+ production Kubernetes clusters processing millions of requests daily. From initial cluster setup to advanced service mesh implementation, I deliver robust container orchestration solutions that scale.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">EKS/AKS Expertise</h4>
                    <p className="text-gray-600">Deep experience with managed Kubernetes on AWS EKS and Azure AKS</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Microservices Architecture</h4>
                    <p className="text-gray-600">Deployed 100+ microservices with service discovery and load balancing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Cost Optimization</h4>
                    <p className="text-gray-600">Right-sizing, spot instances, and cluster autoscaling reducing costs by 40%</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Security Hardening</h4>
                    <p className="text-gray-600">RBAC, network policies, pod security policies, and runtime security</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80"
                alt="Container Management"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Container className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">99.99%</div>
                </div>
                <div className="text-sm text-gray-600">Average Cluster Uptime</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Kubernetes Ecosystem</h2>
            <p className="text-xl text-gray-600">
              Modern tools and platforms for complete container orchestration
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-bold text-lg mb-4 text-purple-600">{tech.name}</h3>
                <ul className="space-y-2">
                  {tech.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Success Story</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold mb-4">Microservices Migration to EKS</h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-purple-100">22 microservices running on EC2 with manual scaling, poor resource utilization, and deployment complexity</p>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-purple-100">Migrated to AWS EKS with Istio service mesh, Helm charts, and ArgoCD for GitOps deployments</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "Resource Efficiency", value: "+65%", improvement: "Better utilization" },
                { metric: "Deployment Time", value: "10 min", improvement: "-90%" },
                { metric: "Infrastructure Cost", value: "-40%", improvement: "$300K saved/yr" },
                { metric: "Auto-Scaling", value: "Real-time", improvement: "0-100 pods in 30s" }
              ].map((result, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-sm text-purple-200 mb-2">{result.metric}</div>
                  <div className="text-3xl font-bold mb-1">{result.value}</div>
                  <div className="text-xs text-green-300">{result.improvement}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Production Kubernetes?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's build a scalable, resilient Kubernetes infrastructure for your microservices
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
